import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Box, Card, Typography } from "@mui/material";
import {
  MyCafeteriaInfoLine,
  MyCafeteriaStatementTitle,
  MyCafeteriaVbkApplicationPrint,
  MyCafeteriaVbkApplicationTable,
} from "my-cafeteria/components";
import { useMyCafeteriaCurrentStatement } from "my-cafeteria/hooks";
import { useMyHrEmployee } from "my-hr/hooks";
import { OcCurrency, OcPageTransition, OcPrintHeader } from "oc/components";
import { useOcCurrentUserState, useOcIsMobile } from "oc/hooks";
import { getFullname } from "oc/utils";

export const MyCafeteriaVbkApplicationPage = () => {
  const history = useHistory();
  const componentRef = useRef(null);
  const isMobile = useOcIsMobile();

  const historyBack = () => {
    history.goBack();
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  let { contractCode, employeeCode } = useOcCurrentUserState();

  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });

  let params = {
    applicationDate: new Date().toLocaleDateString(),
    employeeName: getFullname(employeeData),
    idNumber: employeeData?.taxNumber,
    contractCode: contractCode,
    currentYear: new Date().getFullYear(),
  };

  const { t } = useTranslation();
  const { data: statement } = useMyCafeteriaCurrentStatement({
    contractCode,
  });

  return (
    <OcPageTransition>
      <Box padding={isMobile ? "16px 0px" : "16px"}>
        <OcPrintHeader
          title="VBK Nyilatkozat"
          onBack={historyBack}
          onPrint={handlePrint}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Card
          id="my-cafeteria-summary-card"
          variant="outlined"
          style={{ borderRadius: "4px", border: "1px solid #E3E5E5" }}
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
            padding="16px"
          >
            <Box id="upper-component" paddingBottom="48px">
              <MyCafeteriaStatementTitle
                label={t("MY_CAFETERIA_VBK_STATEMENT")}
              />
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                paddingBottom="16px"
              >
                <Box id="data-block" width={isMobile ? "100%" : "auto"}>
                  <MyCafeteriaInfoLine
                    label={t("OC_CALENDAR_DATE")}
                    value={params?.applicationDate}
                    isMobile={isMobile}
                  />
                  <MyCafeteriaInfoLine
                    label={t("OC_EMPLOYEE_NAME")}
                    value={params?.employeeName ?? ""}
                    isMobile={isMobile}
                  />
                  <MyCafeteriaInfoLine
                    label={t("MY_CAFETERIA_TAX_ID_SIGN_ID")}
                    value={params?.idNumber}
                    isMobile={isMobile}
                  />
                  <MyCafeteriaInfoLine
                    label={t("MY_CAFETERIA_STATEMENT_CONTRACT_CODE")}
                    value={contractCode}
                    isMobile={isMobile}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Box width={isMobile ? "auto" : "221px"} textAlign="right">
                  <Typography variant="h5">
                    {t("MY_CAFETERIA_BUDGET")}
                  </Typography>
                </Box>
                <Box width="16px" />
                <Box>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 700,
                    }}
                  >
                    {OcCurrency({ value: statement?.allAmount })}
                  </Typography>
                </Box>
              </Box>
              <Box height="45px" />
              <Box
                padding={isMobile ? "0px" : "0px 17px"}
                style={{
                  overflowX: isMobile ? "scroll" : "unset",
                  overflowY: isMobile ? "hidden" : "unset",
                  whiteSpace: isMobile ? "nowrap" : "unset",
                }}
              >
                <MyCafeteriaVbkApplicationTable isMobile={isMobile} />
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      <Box style={{ display: "none" }}>
        <MyCafeteriaVbkApplicationPrint
          ref={componentRef}
          applicationDate={params.applicationDate}
          employeeName={params.employeeName ?? ""}
          idNumber={params.idNumber}
          contractCode={params.contractCode}
          currentYear={params.currentYear}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyCafeteriaVbkApplicationPage;
